import {Component,OnInit,ViewChild} from "@angular/core";
import {ActivatedRoute,Router} from "@angular/router";
import {BehaviorSubject,of} from "rxjs";
import {catchError,filter,finalize,timeout} from "rxjs/operators";
import {NgForm} from "@angular/forms";
import {HttpClient,HttpParams} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {environment} from "@environments/environment";

/**
 * Détail d'un abattement
 */
@Component({
    host: {'data-test-id': 'indemnites-abattement-detail'},
    selector: 'indemnites-abattement-detail',
    templateUrl: './indemnites-abattement-detail.component.html'
})
export class IndemnitesAbattementDetailComponent implements OnInit {
    /** Formulaire */
    @ViewChild('form') form: NgForm;

    /** Abattement édité */
    abattement: AbattementDetail;

    /** Liste des horaires */
    listeHoraires: Array<OptionAbattement> = [];

    /** Liste des types d'entité */
    listeTypesEntite: Array<OptionAbattement> = [];

    /** Mode création */
    isCreation: boolean;

    /** Enregistrement en cours */
    isSaving: boolean = false;

    /** Chargement en cours */
    isLoading: boolean = true;

    /** Timer de sécurité du chargement */
    private loadingGuardTimer: ReturnType<typeof setTimeout>;

    /** Liste des différents onglets */
    listeTabItems: Array<PageHeaderItem> = [{
        code: Onglets.GENERALITES,
        libelle: this.translateService.instant('admin.frais.indemnites.abattements.detail.onglets.generalites')
    }];

    /** Onglet courant */
    selectedItem: PageHeaderItem = this.listeTabItems[0];

    /** Liste des actions du bouton flottant */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Référence vers l'enum des onglets pour l'utiliser dans le template */
    Onglets = Onglets;

    /**
     * Constructeur
     */
    constructor(private route: ActivatedRoute,
                private router: Router,
                private http: HttpClient,
                private translateService: TranslateService,
                private toastrService: ToastrService,
                private confirmService: ConfirmService) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        const idAbattement = Number(this.route.snapshot.paramMap.get('idAbattement'));

        //Initialisation du mode
        this.isCreation = idAbattement === 0;

        //Initialisation des actions
        this.defineListeActions();

        //Chargement des données
        this.loadAbattement(idAbattement);
    }

    /**
     * Retour du titre de page
     */
    getTitle(): string {
        return this.isCreation
            ? this.translateService.instant('admin.frais.indemnites.abattements.detail.titleCreation')
            : this.translateService.instant('admin.frais.indemnites.abattements.detail.titleModification');
    }

    /**
     * Changement d'onglet
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        this.selectedItem = selectedItem;
    }

    /**
     * Retour vers la liste
     */
    onGoBack(): void {
        this.router.navigate(['Admin/Frais/Indemnites/Abattements']);
    }

    /**
     * Définition des actions du bouton flottant
     */
    private defineListeActions(): void {
        const liste: FloatingButtonAction[] = [{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.enregistrer',
            doAction: () => this.saveAbattement(),
            //Le formulaire enfant est initialisé après un premier cycle de détection, on évite un basculement true->false dans le même tick
            isDisabled: () => !(this.form?.valid ?? true)
        }];

        if (!this.isCreation) {
            liste.push({
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                doAction: () => this.deleteAbattement()
            });
        }

        this.listeActions.next(liste);
    }

    /**
     * Chargement de l'abattement à partir de l'identifiant
     */
    private loadAbattement(idAbattement: number): void {
        this.isLoading = true;
        this.startLoadingGuard();

        const params = new HttpParams().set('idAbattement', `${idAbattement}`);

        this.http.post<Result<any>>(`${environment.baseUrl}/controller/IJ/consultAbattement`,null,{params})
            .pipe(
                timeout(15000),
                catchError(error => {
                    this.toastrService.error(this.translateService.instant('global.errors.chargement'));
                    return of(null);
                })
            )
            .subscribe({
                next: result => {
                    try {
                        if (!result) {
                            if (!this.isCreation) {
                                this.onGoBack();
                                return;
                            }
                            this.abattement = this.creerAbattementParDefaut();
                            return;
                        }

                        if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                            if (!this.isCreation && !result?.data?.abattement) {
                                this.toastrService.error(this.translateService.instant('global.errors.chargement'));
                                this.onGoBack();
                                return;
                            }

                            //Données des listes de sélection
                            this.listeHoraires = result?.data?.listeHoraire ?? [];
                            this.listeTypesEntite = result?.data?.listeTypeEntite ?? [];

                            //Initialisation de l'abattement
                            this.abattement = new AbattementDetail(result?.data?.abattement ?? {});
                            this.abattement.idAbattement = this.abattement.idAbattement ?? 0;
                            this.abattement.idHoraire = this.abattement.idHoraire ?? 0;
                            this.abattement.idTypeEntite = this.abattement.idTypeEntite ?? 0;
                            this.abattement.nbJours = this.isCreation ? undefined : (this.abattement.nbJours ?? 0);
                            this.abattement.pourcentageAbattement = this.isCreation ? undefined : (this.abattement.pourcentageAbattement ?? 0);
                        } else {
                            if (!this.isCreation) {
                                TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                                this.onGoBack();
                                return;
                            }
                            TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                            this.abattement = this.creerAbattementParDefaut();
                        }
                    } catch (error) {
                        if (!this.isCreation) {
                            this.onGoBack();
                            return;
                        }
                        this.abattement = this.creerAbattementParDefaut();
                    } finally {
                        this.stopLoadingGuard();
                    }
                },
                error: error => {
                    if (!this.isCreation) {
                        this.toastrService.error(this.translateService.instant('global.errors.chargement'));
                        this.onGoBack();
                        return;
                    }
                    this.abattement = this.creerAbattementParDefaut();
                    this.stopLoadingGuard();
                }
            });
    }

    /**
     * Démarre le timer de sécurité pour éviter un chargement infini
     */
    private startLoadingGuard(): void {
        this.stopLoadingGuard(false);
        this.loadingGuardTimer = setTimeout(() => {
            this.isLoading = false;
            if (!this.abattement) {
                this.abattement = this.creerAbattementParDefaut();
            }
            this.toastrService.error(this.translateService.instant('global.errors.chargement'));
        },15000);
    }

    /**
     * Arrête le timer de sécurité et termine le chargement
     */
    private stopLoadingGuard(updateLoading: boolean = true): void {
        if (this.loadingGuardTimer) {
            clearTimeout(this.loadingGuardTimer);
            this.loadingGuardTimer = null;
        }
        if (updateLoading) {
            this.isLoading = false;
        }
    }

    /**
     * Création d'un abattement par défaut
     */
    private creerAbattementParDefaut(): AbattementDetail {
        return new AbattementDetail({
            idAbattement: 0,
            idHoraire: 0,
            idTypeEntite: 0,
            nbJours: undefined,
            pourcentageAbattement: undefined
        });
    }

    /**
     * Enregistrement de l'abattement
     */
    private saveAbattement(): void {
        this.isSaving = true;

        const payload = new AbattementDetail(this.abattement);
        payload.idHoraire = payload.idHoraire ?? 0;
        payload.idTypeEntite = payload.idTypeEntite ?? 0;

        this.http.post<Result<any>>(`${environment.baseUrl}/controller/IJ/saveAbattement`,payload)
            .pipe(finalize(() => this.isSaving = false))
            .subscribe(result => {
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

                    //Retour à la liste comme en V9
                    this.onGoBack();
                } else {
                    TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                }
            });
    }

    /**
     * Suppression de l'abattement
     */
    private deleteAbattement(): void {
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe(() => {
                const payload = new AbattementDetail(this.abattement);
                payload.idHoraire = payload.idHoraire > 0 ? payload.idHoraire : null;
                payload.idTypeEntite = payload.idTypeEntite > 0 ? payload.idTypeEntite : null;

                this.http.post<Result<any>>(`${environment.baseUrl}/controller/IJ/deleteAbattement`,payload).subscribe(result => {
                    if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                        this.toastrService.success(this.translateService.instant('global.success.suppression'));
                        this.onGoBack();
                    } else {
                        TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                    }
                });
            });
    }
}

/**
 * Onglets du détail abattement
 */
export enum Onglets {
    GENERALITES = 'Généralités'
}

/**
 * Option d'une liste déroulante du détail abattement
 */
type OptionAbattement = {
    /** Identifiant */
    idHoraire?: number;

    /** Identifiant */
    idTypeEntite?: number;

    /** Libellé */
    libelle: string;
}

/**
 * Données du détail abattement
 */
export class AbattementDetail {
    /** Identifiant de l'abattement */
    idAbattement: number;

    /** Identifiant de l'horaire */
    idHoraire: number;

    /** Identifiant du type entité */
    idTypeEntite: number;

    /** Nombre de jours */
    nbJours: number;

    /** Pourcentage d'abattement */
    pourcentageAbattement: number;

    /**
     * Constructeur
     */
    constructor(data?: Partial<AbattementDetail>) {
        Object.assign(this,data);
    }
}
